import ModifiedDropdown from '@wandb/weave/common/components/elements/ModifiedDropdown';
import {Button} from '@wandb/weave/components/Button';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import produce from 'immer';
import _ from 'lodash';
import React, {ReactNode} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Popup} from 'semantic-ui-react';

import {DEFAULT_RUNS_SORT, DEFAULT_RUNS_SORT_KEY} from '../../../util/queryts';
import * as QueryTypes from '../../../util/queryTypes';
import * as Run from '../../../util/runs';
import * as UIHelpers from '../../../util/uihelpers';

interface WBTableActionSortButtonProps {
  compact?: boolean;
  disabled?: boolean;
  sortOpen: boolean;
  defaultSort?: QueryTypes.Sort;
  isInReport?: boolean;
  sort: QueryTypes.Sort;
}

export const WBTableActionSortButton = (
  props: WBTableActionSortButtonProps
) => {
  const {isInReport, compact, sort, sortOpen, defaultSort, ...rest} = props;

  const isActive =
    props.sortOpen ||
    !_.isEqual(
      props.sort.keys,
      props.defaultSort?.keys ?? DEFAULT_RUNS_SORT.keys
    );

  const buttonText = 'Sort';
  return (
    <span
      onClick={() => {
        const actionLocation = props.isInReport ? 'report' : 'runs table';
        window.analytics?.track('Sort dropdown clicked', {
          location: actionLocation,
        });
      }}>
      <Button
        {...rest} // Required for use as popup trigger
        icon="sort"
        active={isActive}
        variant="secondary"
        disabled={props.disabled}
        data-test="sort-button"
        tooltip={props.compact ? 'Sort' : ''}
        tooltipProps={{className: 'py-8 px-12'}}
        aria-label="Sorting options">
        {props.compact ? '' : buttonText}
      </Button>
    </span>
  );
};

interface WBTableActionSortDropdownProps {
  sortableKeys: string[];
  loading: boolean;
  sort: QueryTypes.Sort;
  sortKeyIdx: number;

  setSort(sort: QueryTypes.Sort): void;
}

export const WBTableActionSortDropdown = (
  props: WBTableActionSortDropdownProps
) => {
  const {sortableKeys, loading, sort, sortKeyIdx, setSort} = props;
  const sortKey = sort.keys[sortKeyIdx] ?? DEFAULT_RUNS_SORT_KEY;
  const options = UIHelpers.makeOptions(sortableKeys);
  return (
    <ModifiedDropdown
      data-test="sort-dropdown"
      className="sort"
      placeholder="Sort by..."
      search={UIHelpers.searchFunction}
      selection
      lazyLoad
      optionTransform={UIHelpers.beautify}
      itemLimit={100}
      options={options}
      value={Run.keyToString(sortKey.key)}
      disabled={loading}
      loading={loading}
      onChange={(e, {value}) => {
        // We know it's string because we pass in options
        const typedValue: string = value as string;
        const key = Run.keyFromString(typedValue);
        if (key != null) {
          setSort(
            produce(sort, draft => {
              draft.keys[sortKeyIdx].key = key;
            })
          );
        }
      }}
    />
  );
};

interface WBTableActionSortPickerProps {
  defaultSort?: QueryTypes.Sort; // the default sort
  sort: QueryTypes.Sort; // the current sort

  makeDropdown(sortKeyIdx: number): JSX.Element;
  setSort(sort: QueryTypes.Sort): void;
}

export const WBTableActionSortPicker = (
  props: WBTableActionSortPickerProps
) => {
  const {defaultSort, sort, makeDropdown, setSort} = props;
  return (
    <Tailwind>
      <div className="mb-8 font-semibold text-moon-800">Sort runs by...</div>
      <div className="flex flex-col gap-8">
        {sort.keys.map(({key, ascending}, i) => (
          <div
            key={i}
            className={'flex items-center gap-8'}
            data-test="sort-item">
            {sort.keys.length > 1 && (
              <Button
                icon="close"
                variant="ghost"
                onClick={() => {
                  if (sort.keys.length <= 1) {
                    return;
                  }
                  setSort(
                    produce(sort, draft => {
                      draft.keys.splice(i, 1);
                    })
                  );
                }}
              />
            )}
            {makeDropdown(i)}
            <Button
              variant="secondary"
              data-test="sort-order"
              aria-label={`Toggle sort order (currently ${
                ascending ? 'ascending' : 'descending'
              })`}
              icon={`sort-${ascending ? 'ascending' : 'descending'}`}
              onClick={() => {
                setSort(
                  produce(sort, draft => {
                    draft.keys[i].ascending = !draft.keys[i].ascending;
                  })
                );
              }}
            />
          </div>
        ))}
      </div>
      <hr className="mt-16 text-moon-250" />
      <div className="mt-16 flex flex-col gap-8">
        <div
          data-test="sort-add"
          className="cursor-pointer font-semibold	text-teal-600"
          onClick={() => {
            setSort(
              produce(sort, draft => {
                draft.keys.push(
                  defaultSort?.keys?.[0] ?? DEFAULT_RUNS_SORT_KEY
                );
              })
            );
          }}>
          Add another field
        </div>
        <div
          data-test="sort-reset"
          className="cursor-pointer font-semibold text-teal-600"
          onClick={() => setSort(defaultSort ?? DEFAULT_RUNS_SORT)}>
          Reset to default
        </div>
      </div>
    </Tailwind>
  );
};

interface WBTableActionSortProps {
  trigger: (isOpen: boolean) => ReactNode;
  content: ReactNode;
  open: boolean;
  onOpen(): void;
  onClose(): void;
}

export const WBTableActionSort = (props: WBTableActionSortProps) => {
  return (
    <Popup
      basic
      data-test="sort-popup"
      className="wb-table-action-popup"
      on="click"
      position="bottom left"
      open={props.open}
      onOpen={props.onOpen}
      onClose={props.onClose}
      trigger={props.trigger(props.open)}
      content={props.content}
      popperModifiers={{
        preventOverflow: {enabled: false},
        flip: {enabled: false},
      }}
    />
  );
};
